import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, Lock, ShieldCheck, X, BookOpen, Calendar, ClipboardList,
    Bell, ChevronRight, AlertCircle, Sparkles, GraduationCap,
    MapPin, Microscope, CalendarDays, Phone, MessageCircle, HelpCircle,
    ClipboardCheck, CheckSquare,
} from "lucide-react";
import { useTutorAuth } from "../context/TutorAuthContext";
import { SOCIAL } from "../data/siteData";
import { tutorApi, formatApiError } from "../lib/tutorApi";
import DataCompletenessMeter from "../components/DataCompletenessMeter";
import TutorMobileLayout from "../components/tutor/TutorMobileLayout";
import TutorPwaInstallCard from "../components/TutorPwaDownloadButton";

/**
 * V25 Phase 5 — Mobile-First Tutor Dashboard.
 *
 * Premium-app look:
 *  - Welcome hero with profile picture + greeting
 *  - "Pending Actions" hot card (only renders if there's work to do)
 *  - 2x3 grid of stat tiles, each tile a touch-friendly Link with gradient + icon
 *  - Profile completeness inline meter
 *  - Quick actions row (calendar, attendance, support, refer&earn placeholder)
 *  - Sticky bottom nav (5 tabs)
 */
export default function TutorDashboard() {
    const { tutor, refreshMe } = useTutorAuth();
    const [search] = useSearchParams();
    const [showPwdModal, setShowPwdModal] = useState(false);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    useEffect(() => {
        if (!tutor) return;
        if (tutor.must_change_password || search.get("force_pwd") === "1") {
            setShowPwdModal(true);
        }
    }, [tutor, search]);

    useEffect(() => {
        if (!tutor) return;
        (async () => {
            setStatsLoading(true);
            try {
                const { data } = await tutorApi.get("/tutor/me/dashboard-stats");
                setStats(data);
            } catch { setStats(null); }
            finally { setStatsLoading(false); }
        })();
    }, [tutor]);

    if (!tutor) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
        );
    }

    const firstName = (tutor.name || "").split(" ")[0] || tutor.email;
    const initials = (tutor.name || tutor.email).split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();
    const v = (n) => (n === null || n === undefined ? 0 : n);

    // V25 — first-login vs returning greeting + Member Since label
    const isFirstLogin = tutor.is_first_login === true || !tutor.first_login_at;
    const memberSince = (() => {
        const d = tutor.created_at || tutor.approved_application_at;
        if (!d) return null;
        try {
            const date = new Date(d);
            return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
        } catch { return null; }
    })();

    // V34.6 — BookMyTutor-style validity / membership label and pending checks.
    const validityLabel = (() => {
        const d = tutor.subscription_valid_till || tutor.valid_till;
        if (d) {
            try {
                const dt = new Date(d);
                return `Valid till: ${dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
            } catch { /* fall through */ }
        }
        return memberSince ? `Member since ${memberSince}` : (isFirstLogin ? "Welcome aboard" : "Active tutor");
    })();

    // Pending actions
    const pendingActions = [];
    const needsLocation = !tutor.address_lat || !tutor.address_lng || !tutor.permanent_pin_code;
    if (needsLocation) {
        pendingActions.push({ id: "location", label: "Set Your Home Location", to: "/tutor/profile", icon: MapPin, primary: true });
    }
    if (stats?.profile_completeness?.percent < 80) {
        pendingActions.push({ id: "profile", label: "Complete your profile", to: "/tutor/profile", icon: AlertCircle });
    }
    if (v(stats?.my_demos) > 0) {
        pendingActions.push({ id: "demos", label: `${stats.my_demos} demo${stats.my_demos > 1 ? "s" : ""} need action`, to: "/tutor/demos", icon: Calendar });
    }

    // V34.6 — 4×2 grid of feature tiles in BookMyTutor mobile style.
    const featureTiles = [
        { key: "my-demo", label: "My Demo", icon: ClipboardList, to: "/tutor/demos", tone: "bg-rose-50 text-rose-600 ring-rose-200" },
        { key: "completed-demo", label: "Completed Demo", icon: CheckSquare, to: "/tutor/demos?tab=completed", tone: "bg-orange-50 text-orange-600 ring-orange-200" },
        { key: "my-tuitions", label: "My Tuitions", icon: BookOpen, to: "/tutor/tuitions", tone: "bg-emerald-50 text-emerald-600 ring-emerald-200" },
        { key: "attendance", label: "Attendance", icon: ClipboardCheck, to: "/tutor/tuitions?tab=attendance", tone: "bg-rose-50 text-rose-600 ring-rose-200" },
        { key: "study-x", label: "Study X", icon: Microscope, to: "/tutor/leads", tone: "bg-amber-50 text-amber-600 ring-amber-200" },
        { key: "time-table", label: "Time Table", icon: Calendar, to: "/tutor/calendar", tone: "bg-rose-50 text-rose-600 ring-rose-200" },
        { key: "calendar", label: "Calendar", icon: CalendarDays, to: "/tutor/calendar", tone: "bg-rose-50 text-rose-600 ring-rose-200" },
        { key: "refer-earn", label: "Refer & Earn", icon: Sparkles, to: "/tutor/refer", tone: "bg-orange-50 text-orange-600 ring-orange-200" },
    ];

    return (
        <TutorMobileLayout title="Dashboard">
            <TutorPwaInstallCard tutor={tutor} />

            {/* V34.6 — Warm red-gradient hero, BookMyTutor look. */}
            <section className="-mx-4 -mt-4 mb-5 bg-gradient-to-br from-[#E03A2F] via-[#E84A1B] to-[#F35F1A] text-white px-5 pt-6 pb-10 relative overflow-hidden" data-testid="tutor-welcome-hero">
                <div className="absolute -top-12 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
                <div className="flex items-center gap-3 relative">
                    <div className="w-14 h-14 rounded-full bg-white text-[#E03A2F] flex items-center justify-center text-xl font-bold shrink-0">
                        {tutor.profile_picture_url ? (
                            <img src={tutor.profile_picture_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : initials.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-bold text-2xl leading-tight truncate" data-testid="tutor-welcome-name">Hello, {firstName}</div>
                        <div className="text-xs opacity-90 mt-1 inline-flex items-center gap-1.5" data-testid="tutor-validity-label">
                            <ShieldCheck className="w-3.5 h-3.5" /> {validityLabel}
                        </div>
                    </div>
                    <Link to="/tutor/notifications" data-testid="tutor-bell-link"
                        className="bg-white/15 hover:bg-white/25 backdrop-blur rounded-full p-2.5 active:scale-95 transition">
                        <Bell className="w-5 h-5" />
                    </Link>
                </div>
            </section>

            {/* Tutor Guide quick-access — visible on every dashboard load */}
            <Link
                to="/tutor-guide"
                className="mb-4 -mt-4 relative z-10 flex items-center gap-3 rounded-2xl bg-white border border-blue-200 shadow-md p-3 active:scale-[0.99] transition"
                data-testid="tutor-dashboard-guide-link"
            >
                <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center text-lg shrink-0">📘</div>
                <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-slate-900 text-sm truncate">Tutor Guide — Book My Tutor</div>
                    <div className="text-[11px] text-slate-500">Complete 16-step onboarding &amp; payment policy</div>
                </div>
                <div className="text-blue-700 text-lg">›</div>
            </Link>

            {/* My Pending Actions */}
            {pendingActions.length > 0 && (
                <section className="mb-4 -mt-7 relative z-10" data-testid="tutor-pending-actions">
                    <div className="bg-gradient-to-br from-[#E84A1B] to-[#F2734A] rounded-2xl p-5 text-white shadow-xl">
                        <h2 className="font-heading text-xl font-bold mb-3">My Pending Actions</h2>
                        <div className="space-y-2">
                            {pendingActions.map((p) => {
                                const Icon = p.icon || AlertCircle;
                                return (
                                    <Link key={p.id} to={p.to} data-testid={`tutor-pending-${p.id}`}
                                        className={`flex items-center gap-3 ${p.primary ? "bg-white/20" : "bg-white/10"} hover:bg-white/25 rounded-xl px-4 py-3.5 active:scale-[0.98] transition`}>
                                        <Icon className="w-5 h-5 shrink-0" />
                                        <span className="flex-1 font-semibold text-sm">{p.label}</span>
                                        <ChevronRight className="w-5 h-5" />
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Available Tuitions hero card */}
            <Link to="/tutor/leads" data-testid="tutor-available-tuitions-card"
                className="mb-5 bg-white border-2 border-rose-100 rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow active:scale-[0.99] transition">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 text-white flex items-center justify-center shrink-0 shadow-md">
                    <GraduationCap className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg font-bold text-[#0F172A]">Available Tuitions</div>
                    <div className="text-xs text-slate-500 mt-0.5">Find tuitions near you</div>
                </div>
                <ChevronRight className="w-5 h-5 text-rose-500" />
            </Link>

            {/* Profile completeness inline meter */}
            {stats?.profile_completeness && (
                <div className="mb-5">
                    <DataCompletenessMeter percent={stats.profile_completeness.percent} kind="tutor" testid="tutor-dashboard-meter" />
                </div>
            )}

            {/* V34.6 — 4×2 feature tile grid */}
            <section className="grid grid-cols-4 gap-3 mb-6" data-testid="tutor-feature-tiles">
                {statsLoading ? (
                    [...Array(8)].map((_, i) => <div key={i} className="aspect-square rounded-2xl bg-slate-100 animate-pulse" />)
                ) : featureTiles.map((t) => {
                    const Icon = t.icon;
                    return (
                        <Link key={t.key} to={t.to} data-testid={`tutor-tile-${t.key}`}
                            className="flex flex-col items-center gap-1.5 text-center active:scale-95 transition group">
                            <div className={`w-16 h-16 rounded-2xl ${t.tone} ring-1 flex items-center justify-center shadow-sm group-hover:shadow-md transition`}>
                                <Icon className="w-7 h-7" />
                            </div>
                            <div className="text-[11px] font-semibold text-slate-700 leading-tight">{t.label}</div>
                        </Link>
                    );
                })}
            </section>

            {/* Need help */}
            <section className="mb-6" data-testid="tutor-need-help">
                <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-3">Need help?</h2>
                <div className="grid grid-cols-2 gap-3">
                    <a href="tel:+917000530316" data-testid="tutor-need-help-call"
                        className="bg-rose-50 hover:bg-rose-100 rounded-2xl px-4 py-3.5 flex items-center gap-3 active:scale-[0.98] transition">
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                            <Phone className="w-5 h-5 text-rose-600" />
                        </div>
                        <div className="flex-1">
                            <div className="font-bold text-sm text-[#0F172A]">Call Us</div>
                            <div className="text-[11px] text-slate-500">Talk with us</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                    </a>
                    <a href={`https://wa.me/${SOCIAL.whatsapp}`} target="_blank" rel="noreferrer" data-testid="tutor-need-help-whatsapp"
                        className="bg-emerald-50 hover:bg-emerald-100 rounded-2xl px-4 py-3.5 flex items-center gap-3 active:scale-[0.98] transition">
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                            <MessageCircle className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                            <div className="font-bold text-sm text-[#0F172A]">WhatsApp</div>
                            <div className="text-[11px] text-slate-500">Chat with us</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                    </a>
                </div>
                <Link to="/faqs" data-testid="tutor-need-help-faq"
                    className="mt-3 bg-slate-50 hover:bg-slate-100 rounded-2xl px-4 py-3.5 flex items-center gap-3 active:scale-[0.98] transition">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                        <HelpCircle className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 font-bold text-sm text-[#0F172A]">Frequently Asked Questions</div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                </Link>
            </section>

            {/* Footer hint */}
            <p className="text-center text-[10px] text-slate-400 mt-6 pb-2">
                Anand Tutorials · Tutor Portal
            </p>

            {showPwdModal && (
                <ChangePasswordModal
                    must={tutor.must_change_password}
                    onClose={() => setShowPwdModal(false)}
                    onSuccess={() => { setShowPwdModal(false); refreshMe(); }}
                />
            )}
        </TutorMobileLayout>
    );
}

function ChangePasswordModal({ must, onClose, onSuccess }) {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500";

    const submit = async (e) => {
        e.preventDefault();
        if (form.new_password.length < 8) return toast.error("New password must be at least 8 characters");
        if (form.new_password !== form.confirm) return toast.error("Passwords do not match");
        setBusy(true);
        try {
            await tutorApi.post("/tutor/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Password updated.");
            onSuccess();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center px-4 pb-6" data-testid="tutor-change-password-modal">
            <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="bg-[#1E3A8A] text-white p-4 flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] opacity-80">
                            <Lock className="w-4 h-4" /> Security
                        </div>
                        <h2 className="font-bold text-lg leading-tight mt-1">{must ? "Set your password" : "Change password"}</h2>
                        <p className="text-xs opacity-80 mt-1">
                            {must
                                ? "Your account was set up with a temporary password. Please choose your own password now."
                                : "Choose a strong new password (min 8 characters)."}
                        </p>
                    </div>
                    {!must && (
                        <button type="button" onClick={onClose} className="text-white/70 hover:text-white" data-testid="tutor-change-password-close">
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>
                <div className="p-4 space-y-3">
                    <label className="block">
                        <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1 block">Current / temporary password</span>
                        <input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                            data-testid="tutor-change-password-current" className={inputCls} placeholder="••••••••" required />
                    </label>
                    <label className="block">
                        <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1 block">New password</span>
                        <input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                            data-testid="tutor-change-password-new" className={inputCls} placeholder="At least 8 characters" required minLength={8} />
                    </label>
                    <label className="block">
                        <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1 block">Confirm new password</span>
                        <input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                            data-testid="tutor-change-password-confirm" className={inputCls} placeholder="Re-enter new password" required />
                    </label>
                    <button type="submit" disabled={busy} data-testid="tutor-change-password-submit"
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60 flex items-center justify-center gap-2">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Update password
                    </button>
                </div>
            </form>
        </div>
    );
}
